import { EMPTY_OBJECT } from '../../../../constants/common'
import { PAGINATION_ALL_ITEMS_VALUE } from '../../../../constants/pagination'
import { getMassActionExcludeIdParam } from '../../../../utils/common'
import { getIdFilterParam } from '../../../../utils/filter'
import { getPaginationApiParams } from '../../../../utils/pagination'
import { isDraftOpenAction, useDraftCreationOpener } from '../../../Draft'
import { MINIMIZED_MODAL_KEY } from '../../../Modal'
import { createEmailThread } from '../../Email.api'
import { toEmailRecipientSelectItems } from '../../Email.utils'
import {
  EMAIL_CREATION_FORM_FIELD_NAME_TO,
  EMAIL_CREATION_FORM_INITIAL_VALUES,
  EMAIL_CREATION_FORM_RECIPIENT_FIELD_EXPAND_API_PARAM,
  EMAIL_CREATION_FORM_TEMPLATE_FOLDER_MASS,
  isDirtyTableEmailCreationForm,
  isEmailCreationFormAutoAddAdditionalContactsEnabled,
} from '../../EmailCreationForm'
import { EmailCreationPopUp } from '../../EmailCreationPopUp'
import { EmailDraftCreateActionSelectModal } from '../../EmailDraftCreateActionSelectModal'
import { withAdditionalContactRecipients } from '../../EmailRecipientsField'
import { useEmailCreationFormInitialValues } from '../useEmailCreationFormInitialValues'
import { useEmailDraft } from '../useEmailDraft'
import { useEmailGetActiveAccountForCreate } from '../useEmailGetActiveAccountForCreate'
import { useMassMailingAsyncAction } from '../useMassMailingAsyncAction'

import { EMAIL_MASS_MAILING_FORM_INITIAL_VALUES } from './useSendEmail.constants'

export function useSendEmail({
  excludeIds,
  filterValues,
  formFieldsProps,
  getIsDirty = isDirtyTableEmailCreationForm,
  getRecipients,
  massMailing = true,
  selectAllPages,
  selectedIds,
  singleTemplateFolder = EMAIL_CREATION_FORM_TEMPLATE_FOLDER_MASS,
  templateEntityApiParams,
  onCloseEmailFormModal,
  onCloseModal,
  onReload,
  onSetEmailFormModal,
  onSetModal,
  onSubmit = createEmailThread,
  onSuccessSending,
}) {
  const { activeAccountForCreate } = useEmailGetActiveAccountForCreate()

  const {
    checkAsyncMassMailingTaskStatus,
    getAsyncTaskId,
    handleAddFinishMassMailingNotification,
  } = useMassMailingAsyncAction({
    onCloseModal: onCloseEmailFormModal,
    onReload,
  })

  async function loadSelectedUsers({
    additionalFilters,
    additionalParams,
    expandApiParams,
  }) {
    const id = selectAllPages ? undefined : selectedIds

    const exclude_id = getMassActionExcludeIdParam({
      excludeIds,
      selectAllPages,
    })

    const { data } = await getRecipients({
      ...getPaginationApiParams(PAGINATION_ALL_ITEMS_VALUE),
      ...additionalParams,
      ...expandApiParams,
      filters: {
        ...filterValues,
        ...additionalFilters,
        exclude_id,
        id:
          additionalFilters?.id ||
          getIdFilterParam({
            filterValues,
            ids: id,
            selectAllPages,
          }),
      },
    })

    const baseRecipients = toEmailRecipientSelectItems(data.items)

    if (!isEmailCreationFormAutoAddAdditionalContactsEnabled()) {
      return baseRecipients
    }

    return withAdditionalContactRecipients(baseRecipients)
  }

  function handleSuccessSendingEmail(data) {
    onSuccessSending?.()

    const asyncTask = getAsyncTaskId(data.data)

    if (asyncTask) {
      checkAsyncMassMailingTaskStatus(
        { ...data, task: asyncTask },
        handleAddFinishMassMailingNotification
      )

      return
    }

    handleAddFinishMassMailingNotification(data)
  }

  const {
    draft: emailDraft,
    draftId,
    setDraft: setEmailDraft,
  } = useEmailDraft()

  const emailInitial = useEmailCreationFormInitialValues({
    account: activeAccountForCreate,
    initialValues: massMailing
      ? EMAIL_MASS_MAILING_FORM_INITIAL_VALUES
      : EMAIL_CREATION_FORM_INITIAL_VALUES,
  })

  function openEmailCreationModal({
    action,
    additionalFilters = EMPTY_OBJECT,
    additionalParams = EMPTY_OBJECT,
    expandApiParams = EMAIL_CREATION_FORM_RECIPIENT_FIELD_EXPAND_API_PARAM,
    initialValues,
  }) {
    function loadInitialValue() {
      return loadSelectedUsers({
        additionalFilters,
        additionalParams,
        expandApiParams: isEmailCreationFormAutoAddAdditionalContactsEnabled()
          ? expandApiParams
          : undefined,
      })
    }

    // Explicit initial recipients win over loading them from the selection.
    const hasInitialRecipients =
      initialValues[EMAIL_CREATION_FORM_FIELD_NAME_TO]?.length > 0
    const hasSelectedRecipients =
      selectAllPages || selectedIds?.length > 0 || additionalFilters?.id

    onSetEmailFormModal(
      <EmailCreationPopUp
        fieldsProps={{
          ...(hasSelectedRecipients && !hasInitialRecipients
            ? {
                [EMAIL_CREATION_FORM_FIELD_NAME_TO]: {
                  loadInitialValue,
                },
              }
            : null),
        }}
        formFieldsProps={formFieldsProps}
        getContainerIsDirty={() => isDraftOpenAction(action)}
        getIsDirty={getIsDirty}
        initialValues={initialValues}
        minimizedModalSourceId={draftId}
        singleTemplateFolder={singleTemplateFolder}
        templateEntityApiParams={templateEntityApiParams}
        onClose={onCloseEmailFormModal}
        onSubmit={onSubmit}
        onSuccess={handleSuccessSendingEmail}
      />
    )
  }

  function performOpen({
    action,
    additionalFilters,
    additionalParams,
    initialValues,
  } = EMPTY_OBJECT) {
    openEmailCreationModal({
      action,
      additionalFilters,
      additionalParams,
      initialValues: { ...emailInitial, ...initialValues },
    })
  }

  function getInitialValues(payload = EMPTY_OBJECT) {
    return { ...emailInitial, ...payload.initialValues }
  }

  function handleCreateNew() {
    setEmailDraft(null, 0)
  }

  const handleOpenCreationEmail = useDraftCreationOpener({
    DraftPickerModal: EmailDraftCreateActionSelectModal,
    closeModal: onCloseModal,
    draft: emailDraft,
    draftId,
    getInitialValues,
    minimizeKey: MINIMIZED_MODAL_KEY.EMAIL_FORM,
    openModal: performOpen,
    setModal: onSetModal,
    onCreateNew: handleCreateNew,
  })

  return {
    handleOpenCreationEmail,
  }
}
